import { gql } from '@apollo/client';
import client from '../../util/GraphqlClient';
import { CART_SCHEMA } from './cart.api';
import useLocalStorage from '../hooks/useLocalStorage';
import { getTokenFromCookies, getCookie, removeCookie, deleteCookie } from '../hooks/useCookie';

const localStore = useLocalStorage();
const TARIFF_SCHEMA = `
  code
  offerCode
  commitmentDuration
  description
  displayName
  displayPrice
  portIn
  defaultPrice
  discountDuration
  sortOrder
  attributes {
    type
    value
    sortOrder
    createdAt
    icon
    description
  }
  equipmentsPrice
  {
    code
    discountedPrice
  }
  isTVOfferIncluded
  category
`;
const DISCOUNT_SCHEMA = `
catalogDiscountCode
description
value
discountType
code
billingType
discountItemType
vatExcludedAmount
`;
const QUERIES = {
  GET_TARIFFS: gql`
    query getTariffs(
      $acquisitionType: String
      $commitment: Commitment
      $serviceGroupCode: String!
      $simOnly: Boolean
      $portIn: Boolean
      $catalogTariffCode: [String]
      $advertised: Boolean
      $equipmentCode: String
      $reference: String
      $cartType: CartType
      $parentOfferCode: String
      $prequalId:String
      $offerType: String
    ) {
      getTariffs(
        acquisitionType: $acquisitionType
        commitment: $commitment
        serviceGroupCode: $serviceGroupCode
        simOnly: $simOnly
        portIn: $portIn
        catalogTariffCode: $catalogTariffCode
        advertised: $advertised
        equipmentCode : $equipmentCode
        reference: $reference
        cartType: $cartType
        parentOfferCode: $parentOfferCode
        prequalId: $prequalId
        offerType: $offerType
      ) {
        tariffs
        {
          ${TARIFF_SCHEMA}        
        }
        discounts{
          ${DISCOUNT_SCHEMA}
        }
      }
    }
  `,

  ADD_TARIFFS: gql`
    mutation addTariff(
      $reference: String
      $catalogOfferCode: String!
      $catalogTariffPlanCode: String!
      $prequalId: Int
      $portIn: Boolean
      $serviceGroup: String!
      $parentOfferId: String
      $parentSubscriptionId: Int
      $isSecondMobileJourney: Boolean
      $offers: [OfferInput]
      $cartType: CartType
      $offerId: Int
      $subscriptionId: Int
    ) {
      addTariff(
        reference: $reference
        catalogOfferCode: $catalogOfferCode
        catalogTariffPlanCode: $catalogTariffPlanCode
        prequalId: $prequalId
        portIn: $portIn
        serviceGroup:  $serviceGroup
        parentOfferId:  $parentOfferId
        parentSubscriptionId: $parentSubscriptionId
        isSecondMobileJourney:  $isSecondMobileJourney
        offers:  $offers
        cartType: $cartType
        offerId: $offerId
        subscriptionId: $subscriptionId
      ) {
        reference
        ${CART_SCHEMA}
      }
    }
  `,

  REMOVE_TARIFFS: gql`
    mutation removeTariff($reference: String!, $offerId: Int!, $cartType: CartType, $serviceGroup: String) {
      removeTariff(reference: $reference, offerId: $offerId, cartType: $cartType, serviceGroup: $serviceGroup) {
        reference
        ${CART_SCHEMA}
      }
    }
  `,
  GET_HANDSETS: gql`
    query getHandsets(
      $reference: String
      $cartOfferID: Int
      $portIn: Boolean
      $accountId: Int
      $cartType: CartType
      $channel: ChannelType
      $offerType: OfferTypeEnum
      $email: String
    ) {
      getHandsets(
        reference: $reference
        cartOfferID: $cartOfferID
        portIn: $portIn
        accountId: $accountId
        cartType: $cartType
        channel: $channel
        offerType: $offerType
        email: $email
      ) {
        reference
        cartOfferID
        handsets {
          stockLevel
          outOfStock
          image
          imageAltText
          manufacturer
          sortOrder
          model
          price
          slug
          flash {
            description
            color
          }
        }
        manufacturers {
          manufacturer
          logo
          logoAltText
        }
      }
    }
  `,
  GET_RECOMMENDEDBB: gql`
    query recommendedTariff($prequalId: String!, $reference: String!) {
      getRecommendedBBTariffs(prequalId: $prequalId, reference: $reference) {
        tariffs
        {
          ${TARIFF_SCHEMA}        
        }
        discounts{
          ${DISCOUNT_SCHEMA}
        }
      }
    }
  `,
  ADD_BB_TARIFFS: gql`
  mutation addBroadbandTariff(
    $prospectUuid: String!
    $catalogOfferCode: String!
    $catalogTariffPlanCode: String!
    $prequalId: Int!
    $offers: [OfferInput] 
  ) {
    addBroadbandTariff(
      prospectUuid: $prospectUuid
      catalogOfferCode: $catalogOfferCode
      catalogTariffPlanCode: $catalogTariffPlanCode
      prequalId: $prequalId
      offers: $offers
    ) {
      reference
      ${CART_SCHEMA}
    }
  }
`,
};
const sortTariffs = tariffs => {
  const sortOrderData = tariffs
    .slice()
    .filter(item => item.sortOrder !== null)
    .sort((a1, a2) => a1.sortOrder - a2.sortOrder);
  const nonSortOrderData = tariffs
    .slice()
    .filter(item => item.sortOrder === null)
    .sort((a1, a2) => {
      if (a1.displayPrice === a2.displayPrice)
        return parseInt(a1.commitmentDuration, 10) - parseInt(a2.commitmentDuration, 10);
      return a1.displayPrice - a2.displayPrice;
    });
  return [...sortOrderData, ...nonSortOrderData];
};
// to get Mobile and TV tariffs
export const getTariffs = async ({ graphql, variables = {}, staticBB = false }) => {
  let variable = {};
  if (staticBB) variable = { ...variables };
  else {
    variable = {
      ...variables,
      token: getTokenFromCookies(),
      cartType: variables.cartType ? variables.cartType : getCookie('cart_Type'),
    };
    // handling acq type
    if (!variable.acquisitionType) {
      variable.acquisitionType = getCookie('cart_type')
        ? getCookie('cart_type')
        : 'NEW_ACQUISITION';
    }
    if (!getCookie('cart_Type')) delete variable.cartType;
    // eslint-disable-next-line no-lonely-if
    // if (!getCookie('cart_Type')) {
    //   variable = {
    //     ...variables,
    //     token: getTokenFromCookies(), // cartType: getCookie('cart_Type'),
    //   };
    // } else {
    //   variable = {
    //     ...variables,
    //     token: getTokenFromCookies(),
    //     cartType: getCookie('cart_Type'),
    //   };
    // }
  }
  const { data } = await graphql.query({
    query: QUERIES.GET_TARIFFS,
    variables: variable,
  });
  let result = data.getTariffs;
  const { tariffs } = result;

  result = { ...result, ...{ tariffs: sortTariffs(tariffs) } };
  return result;
};

// to add Mobile and TV tariffs
export const addTariffs = async ({ graphql, variables = {} }) => {
  // prepay journey
  if (localStorage.getItem('prospectUuid')) {
    localStorage.removeItem('prospectUuid');
    removeCookie('prepay_prospectUuid');
  }
  // store journey
  if (getCookie('orderId') || getCookie('eir_current_order')) {
    removeCookie('orderId');
    deleteCookie('eir_current_order');
  }
  const { data } = await graphql.mutate({
    mutation: QUERIES.ADD_TARIFFS,
    variables: {
      ...variables,
      token: getTokenFromCookies(),
      cartType: !variables.cartType ? getCookie('cart_Type') : variables.cartType,
    },
  });
  return data;
};

// to add Mobile and TV tariffs
export const addMyEirTariffs = async ({ variables = {}, cookies }) => {
  const { data } = await client.mutate({
    mutation: QUERIES.ADD_TARIFFS,
    variables: {
      ...variables,
      token: getTokenFromCookies(cookies),
      cartType: !variables.cartType ? getCookie('cart_Type') : variables.cartType,
    },
  });
  return data;
};

// to remove Mobile and TV tariffs
export const removeTariffs = async ({ graphql, variables = {} }) => {
  const { data } = await graphql.mutate({
    mutation: QUERIES.REMOVE_TARIFFS,
    variables: {
      ...variables,
      token: getTokenFromCookies(),
      cartType: getCookie('cart_Type'),
    },
  });
  return data;
};
// to get Mobile Handsets
export const getHandsets = async ({ graphql, variables = {} }) => {
  const { data } = await graphql.mutate({
    mutation: QUERIES.GET_HANDSETS,
    variables,
  });
  return data;
};

// to add Broadband  tariffs
export const addBroadbandTariffs = async ({ graphql, variables = {} }) => {
  // prepay journey
  if (localStorage.getItem('prospectUuid')) {
    localStorage.removeItem('prospectUuid');
    removeCookie('prepay_prospectUuid');
  }
  // store journey
  if (getCookie('orderId') || getCookie('eir_current_order')) {
    removeCookie('orderId');
    deleteCookie('eir_current_order');
  }
  const { data } = await graphql.mutate({
    mutation: QUERIES.ADD_BB_TARIFFS,
    variables,
  });
  return data;
};

// to get recommended BB tariffs
export const getRecommendedBBTariffs = async ({ graphql }) => {
  let reference = '';
  let prequalId = '';
  if (localStore.getLocalStorage('PREQUAL_RESULT')) {
    reference = JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT')).reference;
    prequalId = JSON.parse(localStore.getLocalStorage('PREQUAL_RESULT')).prequalId;
  }
  const { data } = await graphql.query({
    query: QUERIES.GET_RECOMMENDEDBB,
    variables: { reference, prequalId },
  });
  let result = data.getRecommendedBBTariffs;
  const { tariffs } = result;

  result = { ...result, ...{ tariffs: sortTariffs(tariffs) } };
  return result.tariffs;
};
